import { calculateWeightTrend } from '~/calculations/weightTrends'
import { ProjectionSummary } from '~/components/feedback/ProjectionSummary'
import { TrendSummary } from '~/components/feedback/TrendSummary'
import { Screen } from '~/components/layout/Screen'
import { useAsyncData } from '~/hooks/useAsyncData'
import { PHASE_LABELS } from '~/models/phase'
import { getOnboardingState } from '~/services/onboardingService'
import { getProjection } from '~/services/projectionService'
import { getTrendObservations, todayFor } from '~/services/weightService'
import { useDatabase } from '~/storage/DatabaseContext'
import { listMeasurementSessions } from '~/storage/repositories/measurementRepository'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import { STORE } from '~/storage/schema'
import { ProgressCharts } from './ProgressCharts'
import styles from './ProgressPage.module.css'

/**
 * Progress.
 *
 * Checkpoint 1.3 shows the trend-first weight view only. Waist, the Navy
 * circumference estimate, measurement quality, strength, projections, and phase
 * boundaries arrive with their own checkpoints, and the screen names what is
 * missing rather than rendering empty cards that imply capability.
 */
export function ProgressPage() {
  const db = useDatabase()

  const { state } = useAsyncData(async () => {
    const now = new Date()
    const reference = todayFor(now)
    const [onboarding, settings, phase, observations, projection, sessions] = await Promise.all([
      getOnboardingState(db),
      getSettings(db, now),
      getActivePhase(db),
      getTrendObservations(db, reference),
      getProjection(db, now),
      listMeasurementSessions(db),
    ])
    const trend = observations.ok ? calculateWeightTrend(observations.value, reference) : undefined
    return { onboarding, settings, phase, trend, projection, observations, sessions, reference }
  }, [
    STORE.profile,
    STORE.phases,
    STORE.weightEntries,
    STORE.settings,
    STORE.goals,
    STORE.measurementSessions,
  ])

  if (state.status === 'loading') {
    return (
      <Screen title="Progress">
        <p>Loading…</p>
      </Screen>
    )
  }

  if (state.status === 'error') {
    return (
      <Screen title="Progress">
        <p role="alert">Project Sola could not read your data. {state.message}</p>
      </Screen>
    )
  }

  const { onboarding, settings, phase, trend, projection, observations, sessions, reference } =
    state.value

  if (!onboarding.ok || !settings.ok) {
    return (
      <Screen title="Progress">
        <p role="alert">Project Sola could not read your data.</p>
      </Screen>
    )
  }

  /* First run only (D-103 §2). Without a phase the trend, the charts and the
     projection all still hold — they are records of weigh-ins, not of a phase —
     and each already renders itself absent when it has nothing. */
  if (onboarding.value.status === 'firstRun') {
    return (
      <Screen title="Progress">
        <p class={styles.lede}>
          Progress appears once Project Sola knows your height and date of birth. Set those up on
          the Today screen.
        </p>
      </Screen>
    )
  }

  return (
    <Screen title="Progress">
      {phase.ok && phase.value !== undefined ? (
        <p class={styles.phase}>
          Current phase: <strong>{PHASE_LABELS[phase.value.type]}</strong>
        </p>
      ) : null}

      {trend === undefined ? null : (
        <TrendSummary trend={trend} unit={settings.value.weightDisplayUnit} />
      )}

      {projection.ok ? (
        <ProjectionSummary projection={projection.value} unit={settings.value.weightDisplayUnit} />
      ) : null}

      <ProgressCharts
        observations={observations.ok ? observations.value : []}
        sessions={sessions.ok ? sessions.value : []}
        phaseStart={phase.ok ? phase.value?.startDate : undefined}
        projection={projection.ok ? projection.value : undefined}
        reference={reference}
        unit={settings.value.weightDisplayUnit}
      />

      <section aria-labelledby="pending-heading">
        <h2 id="pending-heading" class={styles.pendingHeading}>
          Not available yet
        </h2>
        <ul class={styles.pendingList}>
          <li>Sentinel-lift strength summaries</li>
        </ul>
      </section>
    </Screen>
  )
}
